import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Calendar, MapPin } from 'lucide-react';
import { PageShell } from '../components/PageShell';
import { Countdown } from '../components/Countdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRange = (s, e) => {
  if (!s || !e) return '';
  const sd = new Date(s + 'T00:00:00');
  const ed = new Date(e + 'T00:00:00');
  const month = ed.toLocaleString('en-US', { month: 'long' });
  const year = ed.getFullYear();
  const sameMonth = sd.getMonth() === ed.getMonth() && sd.getFullYear() === ed.getFullYear();
  if (sameMonth) return `${sd.getDate()} - ${ed.getDate()} ${month} ${year}`;
  return `${sd.getDate()} ${sd.toLocaleString('en-US',{month:'short'})} – ${ed.getDate()} ${ed.toLocaleString('en-US',{month:'short'})} ${year}`;
};

const HomePage = () => {
  const [series, setSeries] = useState([]);
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/series`).then(r => setSeries(r.data)).catch(() => {});
    axios.get(`${API}/news?limit=3`).then(r => setNews(r.data)).catch(() => {});
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  // Hero priority:
  //   1. A currently-running festival (status = "opened") — soonest end-date wins.
  //   2. The next upcoming festival — soonest start-date wins.
  //   3. is_featured = true acts as a manual override within each status bucket.
  // Archived series are NEVER promoted to the hero (only listed below).
  const pickHero = (list, sortKey) => {
    if (!list.length) return null;
    return [...list].sort((a, b) => {
      const ft = (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      if (ft !== 0) return ft;
      return (a[sortKey] || '').localeCompare(b[sortKey] || '');
    })[0];
  };
  const opened = series.filter(s => s.status === 'opened');
  const upcoming = series.filter(s => s.status === 'upcoming');
  const archived = series.filter(s => s.status === 'archived');
  const heroSeries = pickHero(opened, 'end_date') || pickHero(upcoming, 'start_date') || null;

  return (
    <PageShell>
      {/* HERO — Happening Now (or next upcoming). Falls back to a compact
          default header. The featured-video hero was removed per request. */}
      {heroSeries ? (
        <HeroSeries series={heroSeries} />
      ) : (
        <DefaultHero settings={settings} />
      )}

      {/* CURRENT / UPCOMING SERIES */}
      {opened.length > 0 && (
        <SectionHeader label="Live Now" title="Happening This Week" testid="home-section-opened">
          <SeriesGrid items={opened} variant="opened" />
        </SectionHeader>
      )}
      {upcoming.length > 0 && (
        <SectionHeader label="Save the Date" title="Upcoming Series" testid="home-section-upcoming">
          <SeriesGrid items={upcoming} variant="upcoming" />
        </SectionHeader>
      )}

      {/* PAST FESTIVALS (Triton style grid) */}
      {archived.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-14 border-t border-white/[0.04]" data-testid="home-section-archived">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80 mb-2">Archive</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">Past Series</h2>
            </div>
            <Link to="/series" className="font-heading text-xs tracking-[0.2em] uppercase text-white/50 hover:text-primary-main transition-colors flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <SeriesGrid items={archived.slice(0, 8)} variant="archived" />
        </section>
      )}

      {/* NEWS TEASERS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-14 border-t border-white/[0.04]">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80 mb-2">Latest</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">News &amp; Updates</h2>
          </div>
          <Link to="/news" className="font-heading text-xs tracking-[0.2em] uppercase text-white/50 hover:text-primary-main transition-colors flex items-center gap-1" data-testid="home-news-viewall">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {news.length === 0 ? (
          <div className="text-center py-12 border border-white/[0.04] rounded-2xl">
            <p className="font-body text-sm text-white/40">No news yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {news.map((post) => (
              <Link
                key={post.id}
                to={`/news/${post.slug}`}
                className="group block rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-primary-main/30 transition-colors"
              >
                {post.image_url ? (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={post.image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary-main/10 to-secondary-main/10" />
                )}
                <div className="p-5">
                  <p className="font-mono text-[10px] text-white/30 uppercase tracking-wider">{new Date(post.published_at).toLocaleDateString()}</p>
                  <h3 className="mt-2 font-heading text-base font-bold text-white group-hover:text-primary-main transition-colors line-clamp-2">{post.title}</h3>
                  {post.excerpt && <p className="mt-2 font-body text-sm text-white/50 line-clamp-2">{post.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
};

const HeroSeries = ({ series }) => {
  const isOpened = series.status === 'opened';
  return (
    <section className="relative overflow-hidden border-b border-white/[0.04]" data-testid="home-hero-series">
      {series.hero_url || series.poster_url ? (
        <div className="absolute inset-0">
          <img src={series.hero_url || series.poster_url} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,4,4,0.55) 0%, rgba(8,4,4,0.85) 80%, #080404 100%)' }} />
        </div>
      ) : (
        <>
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] rounded-full opacity-[0.08]" style={{ background: 'radial-gradient(circle, #b91c1c 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[300px] rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #d4af37 0%, transparent 70%)' }} />
        </>
      )}

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ display: isOpened ? 'inline-block' : 'none' }} />
          <span className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/90">
            {isOpened ? 'Happening Now' : series.status === 'upcoming' ? 'Coming Up' : 'Featured'}
          </span>
        </div>

        <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tight text-white leading-[0.85]" data-testid="home-hero-title">
          {series.name}
        </h1>
        <p className="mt-6 font-mono text-lg sm:text-xl tracking-wider text-white/80">{formatRange(series.start_date, series.end_date)}</p>
        {series.location && (
          <p className="mt-2 inline-flex items-center gap-1.5 font-body text-sm text-white/50 uppercase tracking-widest">
            <MapPin className="w-4 h-4" /> {series.location}
          </p>
        )}
        {series.description && !series.description.startsWith('Auto-detected from PokerLens') && (
          <p className="mt-8 max-w-2xl mx-auto font-body text-base sm:text-lg text-white/60 leading-relaxed">{series.description}</p>
        )}

        {series.status === 'upcoming' && (
          <div className="mt-8 flex justify-center">
            <Countdown startDate={series.start_date} variant="hero" testid="home-hero-countdown" />
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={`/series/${series.slug}`}
            data-testid="home-hero-cta"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-heading text-sm tracking-[0.2em] uppercase text-white"
            style={{
              background: 'linear-gradient(135deg, #b91c1c 0%, #8b0000 100%)',
              boxShadow: '0 8px 24px rgba(185,28,28,0.35)',
            }}
          >
            {isOpened ? 'View Live Schedule' : 'See Full Schedule'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/series"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-heading text-sm tracking-[0.2em] uppercase text-white/80 border border-white/15 hover:border-primary-main/60 hover:text-white transition-colors"
          >
            All Series
          </Link>
        </div>
      </div>
    </section>
  );
};

const DefaultHero = ({ settings }) => {
  // Header banner removed — always render the compact branded hero.
  return (
    <section data-testid="home-default-hero" className="relative overflow-hidden border-b border-white/[0.04]">
      <div className="absolute top-0 left-1/4 w-[600px] h-[400px] rounded-full opacity-[0.08]" style={{ background: 'radial-gradient(circle, #b91c1c 0%, transparent 70%)' }} />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h1 className="font-heading text-7xl sm:text-9xl font-black uppercase tracking-tight leading-[0.85]" data-testid="home-hero-title">
          <span className="block text-white">RDPT</span>
          <span className="block text-primary-main mt-2">PLUS</span>
        </h1>
      </div>
    </section>
  );
};

const SectionHeader = ({ label, title, testid, children }) => (
  <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-14 border-t border-white/[0.04]" data-testid={testid}>
    <div className="mb-8">
      <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80 mb-2">{label}</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">{title}</h2>
    </div>
    {children}
  </section>
);

const SeriesGrid = ({ items, variant }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
    {items.map(s => (
      <Link
        key={s.id}
        to={`/series/${s.slug}`}
        data-testid={`home-series-${s.slug}`}
        className="group block rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.02] hover:border-primary-main/40 transition-colors"
      >
        <div className="aspect-[4/3] relative overflow-hidden">
          {s.poster_url ? (
            <img src={s.poster_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a0808 0%, #2d0e0e 100%)' }}>
              <span className="font-heading text-4xl text-primary-main/30 tracking-wider">RDPT+</span>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white group-hover:text-primary-main transition-colors line-clamp-1">{s.name}</h3>
          <p className="mt-1 font-mono text-xs text-white/50">{formatRange(s.start_date, s.end_date)}</p>
        </div>
      </Link>
    ))}
  </div>
);

const OpeningStatement = () => null; // kept as a no-op so admin Site Content tab still works

/**
 * Extract a YouTube video ID from any common URL shape — full youtube.com/watch,
 * youtu.be short, embed, shorts, /live/, and channel-tag wildcards.
 */
const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/(?:embed|v|shorts|live)\/([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

/**
 * VideoHero — used in place of the static header image when a featured video
 * has been configured AND no live/upcoming festival is taking the hero slot.
 * Auto-plays muted on loop, no controls — purely cinematic banner.
 */
const VideoHero = ({ video }) => {
  if (!video || !video.video_url) return null;
  const ytId = extractYouTubeId(video.video_url);
  // Treat anything served by our own /api/files/ endpoint as a self-hosted video
  // even if the storage path lacks an extension. Also accept common mime exts.
  const isMp4 =
    /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(video.video_url) ||
    /\/api\/files\//.test(video.video_url);
  if (!ytId && !isMp4) return null;

  return (
    <section
      data-testid="home-video-hero"
      className="relative overflow-hidden border-b border-white/[0.04]"
      style={{ background: '#080404' }}
    >
      {/* Full-width 16:9 — the video fits perfectly with no zoom or crop. */}
      <div className="relative w-full aspect-video bg-black">
        {ytId ? (
          <iframe
            data-testid="video-hero-iframe"
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1&playsinline=1`}
            title={video.title || 'Featured video'}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
          />
        ) : (
          <video
            data-testid="video-hero-mp4"
            className="absolute inset-0 w-full h-full object-contain"
            src={video.video_url}
            poster={video.thumbnail_url || undefined}
            autoPlay
            muted
            loop
            playsInline
          />
        )}
      </div>
      <div className="h-[2px] w-full" style={{ background: 'linear-gradient(to right, transparent, #b91c1c, #d4af37, #b91c1c, transparent)' }} />
    </section>
  );
};

const FeaturedVideoSection = ({ video }) => {
  if (!video || !video.video_url) return null;
  const ytId = extractYouTubeId(video.video_url);
  const isMp4 = /\.(mp4|webm|mov)(\?|$)/i.test(video.video_url);

  return (
    <section
      data-testid="home-featured-video"
      className="relative border-y border-white/[0.04]"
      style={{ background: '#080404' }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
        {(video.title || video.is_live) && (
          <div className="mb-5 flex items-center gap-3">
            {video.is_live && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
              </span>
            )}
            <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80">Featured</p>
          </div>
        )}
        <div
          className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/[0.08]"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
        >
          {ytId ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
              title={video.title || 'Featured video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : isMp4 ? (
            <video controls className="absolute inset-0 w-full h-full object-cover" poster={video.thumbnail_url || undefined}>
              <source src={video.video_url} />
            </video>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 font-body">
              Unsupported video URL
            </div>
          )}
        </div>
        {video.title && (
          <h3 className="mt-4 font-heading text-xl sm:text-2xl font-bold text-white">{video.title}</h3>
        )}
        {video.description && (
          <p className="mt-2 max-w-3xl font-body text-sm text-white/55 leading-relaxed">{video.description}</p>
        )}
      </div>
    </section>
  );
};

export default HomePage;
